import { ModerationChoices } from '@/components/photovogue/moderation-choices/component';
import type { ModerationChoice } from '@/hooks/use-page-moderation';
import { Checkbox } from '@repo/ui/components/ui/checkbox';
import { cn } from '@repo/ui/lib/utils';
import type { Photo } from '@/services/photovogue';
import { getModerationPreviewUrl } from '@/services/photovogue/moderation-image';
import { PhotoStateBadge } from '@/components/photovogue/state-badge/component';

export interface PhotoListItemProps {
  photo: Photo;
  selectable: boolean;
  moderating?: boolean;
  disabled?: boolean;
  ignoredIds?: string[];
  onChoice?: (id: string, choice: ModerationChoice) => void;
  onPreview?: (photo: Photo) => void;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onOpen: (photo: Photo) => void;
}

export const PhotoListItem = ({
  photo,
  selectable,
  moderating = false,
  disabled = false,
  ignoredIds = [],
  onChoice,
  onPreview,
  selected,
  onToggleSelect,
  onOpen,
}: PhotoListItemProps) => (
  <div
    className={cn(
      'flex flex-wrap items-center gap-3 rounded-lg border bg-white p-2',
      moderating
        ? ignoredIds.includes(photo.id)
          ? 'bg-zinc-50'
          : selected
            ? 'bg-green-50'
            : 'bg-red-50'
        : selected && 'ring-2 ring-zinc-900',
    )}
  >
    {selectable && !moderating && (
      <Checkbox
        checked={selected}
        disabled={disabled}
        onCheckedChange={() => onToggleSelect(photo.id)}
        aria-label={`Select ${photo.title}`}
      />
    )}
    <button
      type="button"
      disabled={disabled}
      onClick={() =>
        onPreview
          ? onPreview(photo)
          : selectable
            ? onToggleSelect(photo.id)
            : onOpen(photo)
      }
      className="flex flex-1 items-center gap-3 text-left"
    >
      <span className="flex h-16 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-zinc-950">
        <img
          src={getModerationPreviewUrl(photo)}
          alt={photo.title}
          loading="lazy"
          className="max-h-full max-w-full object-contain"
        />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{photo.title}</p>
        <p className="truncate text-xs text-cn-muted-foreground">
          {photo.photographer.name} · {photo.category}
        </p>
      </div>
      <PhotoStateBadge state={photo.state} />
    </button>
    {moderating && onChoice && (
      <ModerationChoices
        value={
          ignoredIds.includes(photo.id)
            ? 'ignore'
            : selected
              ? 'approved'
              : 'rejected'
        }
        disabled={disabled}
        onChange={(choice) => onChoice(photo.id, choice)}
      />
    )}
    {onPreview && !moderating && (
      <button
        type="button"
        className="px-3 pb-2 text-xs underline"
        disabled={disabled}
        onClick={() => onOpen(photo)}
      >
        Details
      </button>
    )}
  </div>
);

export default PhotoListItem;
